import { ORDER_METHODS } from '../services/exports/Constants';
import {
  CompanyDetailsInterface,
  OnSiteTable,
  OnSiteTableSelection,
  Order,
  OrderData,
} from '../services/exports/Interfaces';

/**
 * Table ordering (PROD-724) is the order method `on_site`. Until backend !2003 it was `room_service`,
 * with venues (`room_service_location(s)`) and a typed table number (`room_service_details`). Old
 * orders, menus cached before the rename and persisted carts can still carry those names, so the
 * method-keyed data is read through the helpers below.
 */
export const LEGACY_ON_SITE_METHOD = 'room_service';

const ON_SITE_METHOD_VALUES: string[] = [ORDER_METHODS.ON_SITE, LEGACY_ON_SITE_METHOD];

/** How long a table from a QR code stays chosen: one visit, not the next one a week later. */
export const TABLE_SELECTION_TTL_MS = 3 * 60 * 60 * 1000;

/**
 * Why the cart isn't at a table: the `table` query parameter of the menu a table's link opens, or the
 * reason the shop took the cart off its table during the visit (`OrderMethodSlice.tableNotice`).
 */
export enum TableLinkNotice {
  TableUnavailable = 'unavailable',
  TableOrderingOff = 'off',
  /** The visit ran out (3 h from the scan), or a cart saved with room service before table ordering. */
  VisitEnded = 'ended',
  /** The link couldn't be resolved or its location couldn't be loaded (not a 404): scan again. */
  LoadFailed = 'load_failed',
}

export const TABLE_LINK_NOTICE_PARAM = 'table';

export function isOnSiteMethod(method: string | null | undefined): boolean {
  return ON_SITE_METHOD_VALUES.includes(method);
}

/** The shop's own value for a method the backend sent under either name. */
export function normaliseOrderMethod<T extends string | null | undefined>(method: T): ORDER_METHODS | T {
  return isOnSiteMethod(method) ? ORDER_METHODS.ON_SITE : method;
}

/** `list.includes(method)`, matching the on-site method under either name. */
export function includesOrderMethod(list: readonly string[] | null | undefined, method: string | null | undefined) {
  if (!list || method == null) {
    return false;
  }

  return isOnSiteMethod(method) ? list.some(isOnSiteMethod) : list.includes(method);
}

/** `record[method]` for a record keyed by order method, e.g. `order_method_prices`. */
export function valueForOrderMethod<T>(
  record: Partial<Record<string, T>> | null | undefined,
  method: string | null | undefined,
): T | undefined {
  if (!record || method == null) {
    return undefined;
  }

  if (!isOnSiteMethod(method)) {
    return record[method];
  }

  return record[method] ?? ON_SITE_METHOD_VALUES.map((value) => record[value]).find((value) => value != null);
}

/**
 * Whether the company takes table orders: `offers_on_site` is the backend's rule (on, shown, ASAP
 * orders on, a table that is on). Without it (an older backend), the switch and at least one table.
 */
export function companyHasOnSite(company: Partial<CompanyDetailsInterface> | null | undefined): boolean {
  if (typeof company?.offers_on_site === 'boolean') {
    return company.offers_on_site;
  }

  const tables = company?.on_site_tables ?? company?.room_service_locations ?? [];

  return !!(company?.has_on_site ?? company?.has_room_service) && tables.length > 0;
}

/** The copy key of an order's type where only pickup, delivery and table ordering are told apart. */
export function orderTypeKey(method: string | null | undefined): 'pickup' | 'delivery' | 'on_site' {
  if (isOnSiteMethod(method)) {
    return 'on_site';
  }

  return method === ORDER_METHODS.PICKUP ? 'pickup' : 'delivery';
}

/** A table name or line with its whitespace squished, as the backend does before labelling it. */
export function squishTableName(name: string | null | undefined): string {
  return (name ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * "Table 7" for a name that starts with a number, "Terrace 2" as it is: the backend's
 * `OnSiteTable::labelFor`, whitespace squished first. `tableWord` is "Table" translated.
 */
export function formatTableName(name: string | null | undefined, tableWord: string): string {
  const squished = squishTableName(name);

  if (!squished) {
    return '';
  }

  return /^\d/.test(squished) ? `${tableWord} ${squished}` : squished;
}

/** Whether the table chosen from a QR code still applies: the same restaurant, within this visit. */
export function isTableSelectionActive(
  table: OnSiteTableSelection | null | undefined,
  companyId: number | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!table?.id || companyId == null || table.company_id !== companyId) {
    return false;
  }

  const scannedAt = Date.parse(table.scanned_at);

  return Number.isFinite(scannedAt) && now - scannedAt < TABLE_SELECTION_TTL_MS;
}

/**
 * Whether a reset of the cart (an order placed, a logout) takes it off its table: a table order whose
 * visit ran out. While the scan is still good the table stays (`OrderSlice.resetOrder`), so the
 * guest's next round is a table order and the "scan again" note would be wrong. `table` may be the
 * one the reset passes back in place of the cart's.
 */
export function resetLeavesTable(
  order: Partial<OrderData> | null | undefined,
  table: OnSiteTableSelection | null | undefined = order?.on_site_table,
): boolean {
  return order?.method === ORDER_METHODS.ON_SITE && !isTableSelectionActive(table, table?.company_id);
}

/** Whether the cart orders to its table right now: table ordering, with this visit's table at this restaurant. */
export function isOrderAtTable(
  order: Partial<OrderData> | null | undefined,
  companyId: number | null | undefined,
): boolean {
  return order?.method === ORDER_METHODS.ON_SITE && isTableSelectionActive(order.on_site_table, companyId);
}

/**
 * The table line an order was placed with ("7", "Terrasse · 7"): `on_site_table_name`, which the
 * backend freezes at placing (!2015), else an old order's typed number. Never `on_site_table.name`:
 * that is the live table, and a table renamed since would relabel orders already placed.
 */
export function getOrderTableName(order: Partial<Order> | null | undefined): string | null {
  return squishTableName(order?.on_site_table_name ?? order?.room_service_details) || null;
}

type IncomingOrder = Partial<OrderData> &
  Partial<Pick<Order, 'on_site_table_name' | 'room_service_details' | 'room_service_location'>> & {
    on_site_table?: OnSiteTable | OnSiteTableSelection | null;
  };

/**
 * An order as the shop merges it into `cached_order`. Orders from the backend carry the method under
 * either name and the table as the backend has it; the cart keeps the table chosen from the QR code
 * (`OnSiteTableSelection`). The current selection stays when the order is at the same table and the
 * visit hasn't expired; otherwise the order's table (a payment link) becomes the selection from now. Names the shop never
 * sends are dropped.
 */
export function normaliseOrderFields(order: IncomingOrder, current?: OnSiteTableSelection | null): Partial<OrderData> {
  if (!order) {
    return order;
  }

  const {
    room_service_location: legacyLocation,
    room_service_location_id: _legacyLocationId,
    room_service_details: _legacyDetails,
    on_site_table_name: _tableName,
    on_site_table_id: _tableId,
    on_site_table: table,
    ...rest
  } = order as IncomingOrder & { room_service_location_id?: number };

  if (!isOnSiteMethod(rest.method)) {
    // The shop clears (null) or keeps (a selection) the table itself; a backend table is never merged.
    const keepsTable = table === null || (!!table && 'scanned_at' in table);

    return keepsTable ? { ...rest, on_site_table: table as OnSiteTableSelection | null } : rest;
  }

  const incoming = table ?? legacyLocation;
  const selection: OnSiteTableSelection | undefined =
    incoming === undefined
      ? undefined
      : incoming === null
        ? null
        : 'scanned_at' in incoming
          ? incoming
          : current?.id === incoming.id && isTableSelectionActive(current, current.company_id)
            ? // The visit is counted from the scan, not from the orders.
              current
            : {
                id: incoming.id,
                uuid: incoming.uuid,
                name: incoming.name,
                company_id: rest.company_id ?? current?.company_id,
                // Opening the order (a payment link) is this visit; the backend sends no created_at.
                scanned_at: new Date().toISOString(),
              };

  return {
    ...rest,
    method: ORDER_METHODS.ON_SITE,
    ...(selection !== undefined && { on_site_table: selection }),
  };
}

/**
 * A persisted cart from before table ordering: a room-service method or venue can't be turned into a
 * table (only a QR code chooses one), so the cart goes back to pickup.
 */
export function migratePersistedOrder<T extends Record<string, unknown>>(cachedOrder: T): T {
  if (!cachedOrder) {
    return cachedOrder;
  }

  const {
    room_service_location: _location,
    room_service_location_id: _locationId,
    room_service_details: _details,
    ...rest
  } = cachedOrder;
  const hasTable = !!(rest.on_site_table as OnSiteTableSelection)?.scanned_at;

  return {
    ...rest,
    ...(isOnSiteMethod(rest.method as string) && !hasTable && { method: ORDER_METHODS.PICKUP }),
  } as unknown as T;
}

/** What a table order sends: the table's id, never a time (table orders are as soon as possible). */
export function toOrderRequestFields(order: Partial<OrderData>): Partial<OrderData> {
  const { on_site_table: table, on_site_table_id: _tableId, ...rest } = order;

  if (rest.method !== ORDER_METHODS.ON_SITE) {
    return rest;
  }

  return { ...rest, on_site_table_id: table?.id ?? null, scheduled_for: null };
}
